import React, { useCallback, useMemo } from 'react';

import type { MessageDescriptor } from 'react-intl';
import { di } from 'react-magnetic-di';

import FeatureGates from '@atlaskit/feature-gate-js-client';
import { FlagsProvider } from '@atlaskit/flag';
import { fg } from '@atlaskit/platform-feature-flags';
import { AgenticPlanToolInChat } from '@atlassian/agentic-planning/AgenticPlanToolInChat.tsx';
import { MultipleChoiceQuestionsToolInChat } from '@atlassian/agentic-planning/MultipleChoiceQuestionsToolInChat.tsx';
import { validateJiraAdminAgentAction } from '@atlassian/ai-mate-jira-admin-agent-api/validateJiraAdminAgentAction';
import { JiraAdminAgentRenderer } from '@atlassian/ai-mate-jira-admin-agent/JiraAdminAgentRenderer';
import {
	AIFC_CONFLUENCE_WHITEBOARD_DIAGRAMMING_KEY,
	CONFLUENCE_AIFC_DATABASE_CREATE_KEY,
	CONFLUENCE_AIFC_DATABASE_UPDATE_KEY,
	CONFLUENCE_AIFC_DEEP_RESEARCH_PAGE_CREATE_KEY,
	CONFLUENCE_AIFC_PAGE_CREATE_KEY,
	CONFLUENCE_AIFC_PAGE_UPDATE_KEY,
	CONFLUENCE_AIFC_SLIDES_CREATE_KEY,
	CONFLUENCE_AIFC_SLIDES_UPDATE_KEY,
	CONFLUENCE_AIFC_SPACE_CREATE_KEY,
	CONFLUENCE_AIFC_SPACE_UPDATE_KEY,
	CONFLUENCE_AIFC_WHITEBOARD_CREATE_KEY,
	CONFLUENCE_AIFC_WHITEBOARD_TO_PAGE_KEY,
	CONFLUENCE_AIFC_WHITEBOARD_UPDATE_KEY,
	CONFLUENCE_WHITEBOARD_BRAINSTORMING_KEY,
	DYNAMIC_UI_CREATE_KEY,
	IMAGE_CREATE_KEY,
	LEGACY_CONFLUENCE_WHITEBOARD_DIAGRAMMING_KEY,
} from '@atlassian/conversation-assistant-action-helpers/utils/confluence-actions';
import { logException } from '@atlassian/conversation-assistant-instrumentation/services/sentry';
import { CreateKBCard } from '@atlassian/conversation-assistant-jsm-create-kb-card/create-kb/CreateKBCard';
import {
	ActionKeys,
	ActionPredicateKeys,
	JSM_CREATE_SURVEY_KEY,
} from '@atlassian/conversation-assistant-message-actions-api/constants';
import {
	type ActionRendererKeyToPayload,
	type ActionRendererPredicateKeyToPayload,
	type ActionRendererPredicateRegistry,
	type ActionRendererProduct,
	type ActionRendererRegistry,
	type ActionRendererRegistryEntry,
	type ActionValidationFailure,
	type ActionWith,
	type GetOpaqueActionRenderer,
	type OpaqueReadOnlyActionRenderer,
} from '@atlassian/conversation-assistant-message-actions-api/types';
import {
	isActionValidationFailureError,
	useActionRenderer as useActionRendererWithRegistry,
} from '@atlassian/conversation-assistant-message-actions-api/use-action-renderer';
import { type MessageAction } from '@atlassian/conversation-assistant-service-api/types/message-actions';
import { JiraUpdateWorkItemAllFieldTypesActionRenderer } from '@atlassian/jira-update-work-item-all-field-types-action-renderer/JiraUpdateWorkItemAllFieldTypesActionRenderer';
import { JiraUpdateWorkItemAllFieldTypesActionSkeleton } from '@atlassian/jira-update-work-item-all-field-types-action-renderer/JiraUpdateWorkItemAllFieldTypesActionSkeleton';
import { validateJiraUpdateWorkAllFieldTypesAction } from '@atlassian/jira-update-work-item-all-field-types-action-renderer/utils';
import { JiraUpdateWorkItemFieldsActionRenderer } from '@atlassian/jira-update-work-item-fields-action-renderer/JiraUpdateWorkItemFieldsActionRenderer';
import { JiraUpdateWorkItemFieldsActionSkeleton } from '@atlassian/jira-update-work-item-fields-action-renderer/JiraUpdateWorkItemFieldsActionSkeleton';
import { validateJiraUpdateWorkItemFieldsAction } from '@atlassian/jira-update-work-item-fields-action-renderer/utils';

import { jiraPlansActionData } from '../../../common/utils/plan-action-data';
import { AskQuestionCompletedView } from '../../ask-question-completed-view/AskQuestionCompletedView';
import {
	AskQuestionReadOnlyRendererAsync,
	AskQuestionRendererAsync,
} from '../../ask-question/async';
import { CreateProjectProgressUpdateActionRenderer } from '../../atlassian-project-status-update-action-renderer/main';
import { CreateProjectProgressUpdateActionSkeleton } from '../../atlassian-project-status-update-action-renderer/skeleton';
import { validateCreateProjectProgressUpdateAction as isCreateProjectProgressUpdateAction } from '../../atlassian-project-status-update-action-renderer/validateCreateProjectProgressUpdateAction';
import { AtlassianTeamUpdateArchiveAction } from '../../atlassian-team-update-archive';
import { CapacityPlanningActionRenderer } from '../../capacity-planning';
import { ConfluenceAifcAdfUpdateAction } from '../../confluence-aifc-adf-update';
import { ConfluenceFolderCreateAction } from '../../confluence-folder-create';
import { ConfluencePageAddLabelAction } from '../../confluence-page-add-label';
import { ConfluencePageCommentAction } from '../../confluence-page-comment';
import { ConfluencePageCreateAction } from '../../confluence-page-create';
import { ConfluencePageMoveAction } from '../../confluence-page-move';
import { ConfluencePageRenameAction } from '../../confluence-page-rename';
import { DeepResearchActions } from '../../deep-research-actions';
import { ExecutePythonRendererAsync } from '../../execute-python/async';
import { FocusAskCreateAction } from '../../focus-ask-create/FocusAskCreateAction';
import { GoogleCalendarEventCreateRendererAsync } from '../../google-calendar-event-create/async';
import { GoogleDocCreateRendererAsync } from '../../google-doc-create/GoogleDocCreateRendererAsync';
import { HamOnboardingHardwareLink } from '../../ham-onboarding-hardware-link/HamOnboardingHardwareLink';
import { HiringManagerToolUIAction } from '../../hiring-manager-tool-action-renderer';
import { ImageSearchActionAsync } from '../../image-search/async';
import { JiraCreateSprintAction } from '../../jira-create-sprint/JiraCreateSprint';
import { JiraSmartIssueCreateAllFieldAction } from '../../jira-create-work-items-all-field-types';
import { JiraEpicsSuggestAction } from '../../jira-epics-suggest';
import { JiraFindIssuesAction } from '../../jira-find-issues';
import { JiraIssueAssignAction } from '../../jira-issue-assign';
import { JiraIssueDeleteAction } from '../../jira-issue-delete';
import { JiraIssueMoveAction } from '../../jira-issue-move';
import { JiraIssueTransitionAction } from '../../jira-issue-transition';
import { JiraMoveToSprintAction } from '../../jira-move-to-sprint/JiraMoveToSprint';
import { AddDependencyAction } from '../../jira-plans-add-dependency';
import { AddProgramBoardAction } from '../../jira-plans-add-program-board';
import { CreateReleaseAction } from '../../jira-plans-create-release';
import { CreateWorkItemAction } from '../../jira-plans-create-work-item';
import { DeleteProgramBoardAction } from '../../jira-plans-delete-program-board';
import { ManageFeaturesAction } from '../../jira-plans-manage-features';
import { RemoveDependencyAction } from '../../jira-plans-remove-dependency';
import { UpdateWorkSourceAction } from '../../jira-plans-update-work-source';
import { JiraStartSprintAction } from '../../jira-start-sprint/JiraStartSprint';
import { JiraVersionCreateAction } from '../../jira-version-create';
import { JiraViewCreateAndConfigureAction } from '../../jira-view-create-and-configure/JiraViewCreateAndConfigureAction';
import { JiraWorkflowEditorUIAction } from '../../jira-workflow-wizard-actions';
import { JsmCreateEmbeddedRequestAction } from '../../jsm-create-embedded-request';
import { JsmCreateQueueAction } from '../../jsm-create-queue-action/JsmCreateQueueAction';
import { JsmCreateQueueGroupAction } from '../../jsm-create-queue-group-action/JsmCreateQueueGroupAction';
import { JsmCreateRequestAction } from '../../jsm-create-request';
import { JsmCreateRequestTypeAction } from '../../jsm-create-request-type-action/JsmCreateRequestTypeAction';
import { JsmJourneyBuilderUIActionAsync } from '../../jsm-journey-builder-actions/async';
import { JsmLivechatAction } from '../../jsm-livechat';
import { JsmSelectHardwareTypesAction } from '../../jsm-select-hardware-types-action/JsmSelectHardwareTypesAction';
import { JsmSolutionComposerPlanAction } from '../../jsm-solution-composer-plan';
import { JsmUpdateQueueAction } from '../../jsm-update-queue-action/JsmUpdateQueueAction';
import { JsmUpdateQueueGroupAction } from '../../jsm-update-queue-group-action/JsmUpdateQueueGroupAction';
import { JsmUpdateRequestTypeAction } from '../../jsm-update-request-type-action/JsmUpdateRequestTypeAction';
import { JsmUpdateRequestTypePortalGroupsAction } from '../../jsm-update-request-type-portal-groups-action/JsmUpdateRequestTypePortalGroupsAction';
import { JsmUpdateRequestTypeWorkflowAction } from '../../jsm-update-request-type-workflow-action/JsmUpdateRequestTypeWorkflowAction';
import {
	PlanCompleteReadOnlyRendererAsync,
	PlanCompleteRendererAsync,
} from '../../plan-complete/async';
import { RequireInformationUIAction } from '../../require-information-tool';
import { ResumptionRendererAsync } from '../../resumption-ui/ResumptionRendererAsync';
import { ScheduledChatAction } from '../../scheduled-chat/ScheduledChatActionCard';
import { SlackSendChannelMessageRendererAsync } from '../../slack-send-channel-message/SlackSendChannelMessageRendererAsync';
import { StoreRecommendationAction } from '../../store-recommendation';
import { StudioAutomationCreateRuleAction } from '../../studio-automation-create-rule/StudioAutomationCreateRuleAction';
import { StudioAutomationTriggerRuleBuildAction } from '../../studio-automation-trigger-rule-build';
import { StudioCreateAgentRenderer } from '../../studio-create-agent/StudioCreateAgentRenderer';
import { StudioCustomSkillRenderer } from '../../studio-custom-skill/StudioCustomSkillRenderer';
import { StudioLandingPageRedirect } from '../../studio-landing-page-redirect';
import { StudioTriggerSolutionBuildAction } from '../../studio-trigger-solution-build';
import { StudioUpdateAgentDraftAction } from '../../studio-update-agent-draft';
import { UpdateAgentConfigurationAction } from '../../studio-update-agent/update-agent-configuration';
import { StudioWorkspaceUpdateAgentDraftAction } from '../../studio-update-agent/update-draft-agent';

import { readonlyTitleMessages } from './readonly-title-messages';

const jiraGatedRegistery: ActionRendererRegistry<ActionRendererKeyToPayload> = {
	[ActionKeys.UpdateWorkItemFields]: {
		readonlyTitle: readonlyTitleMessages.updateWorkItemFields,
		renderer: JiraUpdateWorkItemFieldsActionRenderer,
		fallback: JiraUpdateWorkItemFieldsActionSkeleton,
		validate: validateJiraUpdateWorkItemFieldsAction,
		getAdoptedObjects: (action) => [{ objectType: 'work-item', objectId: action.data.issueId }],
	},
	[ActionKeys.CreateProjectProgressUpdate]: {
		readonlyTitle: readonlyTitleMessages.createProjectProgressUpdate,
		renderer: CreateProjectProgressUpdateActionRenderer,
		fallback: CreateProjectProgressUpdateActionSkeleton,
		validate: isCreateProjectProgressUpdateAction,
		getAdoptedObjects: (action) => [{ objectType: 'project', objectId: action.data.projectId }],
	},
	[ActionKeys.UpdateWorkItemAllFieldTypes]: {
		readonlyTitle: readonlyTitleMessages.updateWorkItems,
		renderer: JiraUpdateWorkItemAllFieldTypesActionRenderer,
		fallback: JiraUpdateWorkItemAllFieldTypesActionSkeleton,
		validate: validateJiraUpdateWorkAllFieldTypesAction,
		getAdoptedObjects: (action) =>
			(action.data.updates ?? []).map((update) => ({
				objectType: 'work-item',
				objectId: update.issueIdOrKey,
			})),
	},
};

const hamOnboardingSetupCompleteEntry = {
	readonlyTitle: readonlyTitleMessages.completeSetup,
	readonlyProduct: 'jsm' as const,
	condition: () => fg('itam_ftw'),
	renderer: (props: React.ComponentProps<typeof HamOnboardingHardwareLink>) => {
		di(HamOnboardingHardwareLink);
		return <HamOnboardingHardwareLink {...props} />;
	},
};

type GetRovoToolsResumptionMigrationEnabled = () => boolean;

const defaultGetRovoToolsResumptionMigrationEnabled: GetRovoToolsResumptionMigrationEnabled = () =>
	FeatureGates.getExperimentValue('rovo_tools_resumption_migration_exp', 'isEnabled', false);

const createRovoToolsResumptionMigrationRegistry = (
	getRovoToolsResumptionMigrationEnabled: GetRovoToolsResumptionMigrationEnabled,
): ActionRendererRegistry<ActionRendererKeyToPayload> => ({
	[ActionKeys.SlackSendChannelMessage]: {
		readonlyTitle: readonlyTitleMessages.sendSlackMessage,
		readonlyProduct: 'slack',
		renderer: SlackSendChannelMessageRendererAsync,
		condition: getRovoToolsResumptionMigrationEnabled,
	},
	[ActionKeys.GoogleCalendarEventCreate]: {
		readonlyTitle: readonlyTitleMessages.createCalendarEvent,
		readonlyProduct: 'google-calendar',
		renderer: GoogleCalendarEventCreateRendererAsync,
		condition: getRovoToolsResumptionMigrationEnabled,
	},
	[ActionKeys.GoogleDocCreate]: {
		readonlyTitle: readonlyTitleMessages.createDocument,
		readonlyProduct: 'google-doc',
		renderer: GoogleDocCreateRendererAsync,
		condition: getRovoToolsResumptionMigrationEnabled,
	},
});

const unifiedRegistery: ActionRendererRegistry<ActionRendererKeyToPayload> = {
	// Jira Actions
	[ActionKeys.JiraFindIssues]: {
		readonlyTitle: readonlyTitleMessages.findWorkItems,
		renderer: (props) => {
			di(JiraFindIssuesAction);
			return <JiraFindIssuesAction {...props} />;
		},
	},
	[ActionKeys.JiraSmartIssueCreateAllField]: {
		readonlyTitle: readonlyTitleMessages.createWorkItem,
		renderer: (props) => {
			di(FlagsProvider, JiraSmartIssueCreateAllFieldAction);
			if (fg('jira-aifc-migrate-action-renderer')) {
				return <JiraSmartIssueCreateAllFieldAction {...props} />;
			}

			// Otherwise defer to the streaming action renderer
			return undefined;
		},
	},
	[ActionKeys.JiraAdminAgent]: {
		readonlyTitle: readonlyTitleMessages.jiraAdminAction,
		renderer: (props) => {
			return <JiraAdminAgentRenderer {...props} />;
		},
		validate: (action) => {
			const payload = validateJiraAdminAgentAction(action.data);
			if (payload) {
				return {
					isValid: true,
					action: action as ActionWith<
						ActionRendererKeyToPayload[typeof ActionKeys.JiraAdminAgent]
					>,
				};
			}
			return {
				isValid: false,
				error: new Error(`Invalid ${action.key || ''} action payload`),
			};
		},
		condition: () => fg('fe_jira_admin_agent_eap'),
	},
	[ActionKeys.JiraIssueTransition]: {
		readonlyTitle: readonlyTitleMessages.transitionWorkItem,
		renderer: JiraIssueTransitionAction,
		getAdoptedObjects: (action) => [{ objectType: 'work-item', objectId: action.data.ari }],
	},
	[ActionKeys.JiraEpicsSuggest]: {
		readonlyTitle: readonlyTitleMessages.suggestEpics,
		renderer: JiraEpicsSuggestAction,
	},
	[ActionKeys.JiraIssueDelete]: {
		readonlyTitle: readonlyTitleMessages.deleteWorkItem,
		renderer: JiraIssueDeleteAction,
		getAdoptedObjects: (action) =>
			(action.data.issues ?? []).map((issue) => ({
				objectType: 'work-item',
				objectId: issue.issueKey,
			})),
	},
	[ActionKeys.JiraIssueMove]: {
		readonlyTitle: readonlyTitleMessages.moveWorkItem,
		renderer: JiraIssueMoveAction,
		getAdoptedObjects: (action) =>
			(action.data.issues ?? []).map((issue) => ({
				objectType: 'work-item',
				objectId: issue.issueKey,
			})),
	},
	[ActionKeys.JiraIssueAssign]: {
		readonlyTitle: readonlyTitleMessages.assignWorkItem,
		renderer: JiraIssueAssignAction,
		getAdoptedObjects: (action) => [{ objectType: 'work-item', objectId: action.data.ari }],
	},
	[ActionKeys.JiraVersionCreate]: {
		readonlyTitle: readonlyTitleMessages.createRelease,
		renderer: JiraVersionCreateAction,
	},
	[ActionKeys.JiraViewCreateAndConfigure]: {
		readonlyTitle: readonlyTitleMessages.createView,
		renderer: JiraViewCreateAndConfigureAction,
	},
	[ActionKeys.JiraCreateSprint]: {
		readonlyTitle: readonlyTitleMessages.createSprint,
		renderer: JiraCreateSprintAction,
	},
	[ActionKeys.JiraStartSprint]: {
		readonlyTitle: readonlyTitleMessages.startSprint,
		renderer: JiraStartSprintAction,
	},
	[ActionKeys.JiraMoveToSprint]: {
		readonlyTitle: readonlyTitleMessages.moveToSprint,
		renderer: JiraMoveToSprintAction,
	},
	[ActionKeys.ManageCapacityPlan]: {
		readonlyTitle: readonlyTitleMessages.manageCapacityPlan,
		readonlyProduct: 'capacity-planning',
		renderer: (props) => {
			di(FlagsProvider);
			return fg('ai_features_in_icp_m1') ? (
				<CapacityPlanningActionRenderer {...props} />
			) : undefined;
		},
	},
	[ActionKeys.JiraWorkflowEditor]: {
		readonlyTitle: readonlyTitleMessages.updateWorkflow,
		renderer: JiraWorkflowEditorUIAction,
	},
	// Confluence Actions
	[ActionKeys.ConfluencePageCreate]: {
		readonlyTitle: readonlyTitleMessages.createPage,
		readonlyProduct: 'confluence',
		renderer: ConfluencePageCreateAction,
	},
	[ActionKeys.ConfluenceFolderCreate]: {
		readonlyTitle: readonlyTitleMessages.createFolder,
		readonlyProduct: 'confluence',
		renderer: ConfluenceFolderCreateAction,
		condition: () => fg('confluence_folder_create_action_custom_ui'),
	},
	[ActionKeys.ConfluencePageComment]: {
		readonlyTitle: readonlyTitleMessages.commentOnPage,
		readonlyProduct: 'confluence',
		renderer: ConfluencePageCommentAction,
		getAdoptedObjects: (action) => [{ objectType: 'page', objectId: action.data.contentId }],
	},
	[ActionKeys.AifcAdfUpdate]: {
		readonlyTitle: readonlyTitleMessages.editContent,
		readonlyProduct: 'confluence',
		renderer: (props) =>
			// eslint-disable-next-line @atlaskit/platform/use-recommended-utils
			FeatureGates.checkGate('aifc_adf_update_tool') ? (
				<ConfluenceAifcAdfUpdateAction {...props} />
			) : undefined,
		getAdoptedObjects: (action) => [{ objectType: 'page', objectId: action.data.contentId }],
	},
	[ActionKeys.ConfluencePageAddLabel]: {
		readonlyTitle: readonlyTitleMessages.addLabel,
		readonlyProduct: 'confluence',
		renderer: (props) => {
			di(FlagsProvider);
			// eslint-disable-next-line @atlaskit/platform/use-recommended-utils
			return FeatureGates.getExperimentValue(
				'confluence_add_page_label_rovo_action_custom_ui',
				'isEnabled',
				false,
			) ? (
				<ConfluencePageAddLabelAction {...props} />
			) : undefined;
		},
		getAdoptedObjects: (action) => [{ objectType: 'page', objectId: action.data.id }],
	},
	[ActionKeys.ConfluencePageRename]: {
		readonlyTitle: readonlyTitleMessages.renamePage,
		readonlyProduct: 'confluence',
		// eslint-disable-next-line @atlaskit/platform/use-recommended-utils
		condition: () =>
			FeatureGates.getExperimentValue(
				'confluence_rename_page_rovo_action_custom_ui',
				'isEnabled',
				false,
			),
		renderer: (props) => {
			di(ConfluencePageRenameAction);
			return <ConfluencePageRenameAction {...props} />;
		},
		getAdoptedObjects: (action) => [{ objectType: 'page', objectId: action.data.documentId }],
	},
	[ActionKeys.ConfluencePageMove]: {
		readonlyTitle: readonlyTitleMessages.movePage,
		readonlyProduct: 'confluence',
		// eslint-disable-next-line @atlaskit/platform/use-recommended-utils
		condition: () =>
			FeatureGates.getExperimentValue(
				'confluence_move_page_rovo_action_custom_ui',
				'isEnabled',
				false,
			),
		renderer: (props) => {
			di(ConfluencePageMoveAction);
			return <ConfluencePageMoveAction {...props} />;
		},
		getAdoptedObjects: (action) => [{ objectType: 'page', objectId: action.data.documentId }],
	},
	[ActionKeys.JsmJourneyBuilder]: {
		readonlyTitle: readonlyTitleMessages.createRequest,
		readonlyProduct: 'jsm',
		renderer: (props) => {
			di(JsmJourneyBuilderUIActionAsync);
			return <JsmJourneyBuilderUIActionAsync {...props} />;
		},
	},
	[ActionKeys.JsmCreateRequest]: {
		readonlyTitle: readonlyTitleMessages.createRequest,
		readonlyProduct: 'jsm',
		renderer: (props) => {
			di(FlagsProvider, JsmCreateRequestAction);
			return fg('service_agent_jsm_request_custom_ui') ? (
				<JsmCreateRequestAction {...props} />
			) : undefined;
		},
	},
	[ActionKeys.JsmCreateRequestV2]: {
		readonlyTitle: readonlyTitleMessages.createRequest,
		readonlyProduct: 'jsm',
		renderer: JsmCreateRequestAction,
	},
	[ActionKeys.CreateRequest]: {
		readonlyTitle: readonlyTitleMessages.createRequest,
		readonlyProduct: 'jsm',
		renderer: JsmCreateRequestAction,
	},
	[ActionKeys.JsmCreateEmbeddedRequest]: {
		readonlyTitle: readonlyTitleMessages.createRequest,
		readonlyProduct: 'jsm',
		renderer: JsmCreateEmbeddedRequestAction,
	},
	[ActionKeys.JsmSolutionComposerPlanSummary]: {
		readonlyTitle: readonlyTitleMessages.buildSolution,
		readonlyProduct: 'jsm',
		renderer: JsmSolutionComposerPlanAction,
	},
	[ActionKeys.JsmSuggestAndAddFieldsToRequestType]: {
		readonlyTitle: readonlyTitleMessages.updateRequestType,
		readonlyProduct: 'jsm',
		renderer: JsmUpdateRequestTypeAction,
	},
	[ActionKeys.JsmAtlassianProjectCreateJsmRequestTypeWithFields]: {
		readonlyTitle: readonlyTitleMessages.createRequestType,
		readonlyProduct: 'jsm',
		renderer: JsmCreateRequestTypeAction,
	},
	[ActionKeys.JsmAtlassianProjectCreateJsmQueue]: {
		readonlyTitle: readonlyTitleMessages.createQueue,
		readonlyProduct: 'jsm',
		renderer: JsmCreateQueueAction,
	},
	[ActionKeys.JsmAtlassianProjectCreateJsmQueueGroup]: {
		readonlyTitle: readonlyTitleMessages.createQueueGroup,
		readonlyProduct: 'jsm',
		renderer: JsmCreateQueueGroupAction,
	},
	[ActionKeys.JsmAtlassianProjectUpdateJsmQueue]: {
		readonlyTitle: readonlyTitleMessages.updateQueue,
		readonlyProduct: 'jsm',
		renderer: JsmUpdateQueueAction,
	},
	[ActionKeys.JsmAtlassianProjectUpdateJsmQueueGroup]: {
		readonlyTitle: readonlyTitleMessages.updateQueueGroup,
		readonlyProduct: 'jsm',
		renderer: JsmUpdateQueueGroupAction,
	},
	[ActionKeys.JsmAtlassianProjectUpdateRequestTypePortalGroups]: {
		readonlyTitle: readonlyTitleMessages.updatePortalGroups,
		readonlyProduct: 'jsm',
		renderer: JsmUpdateRequestTypePortalGroupsAction,
	},
	[ActionKeys.JsmAtlassianRequestTypeUpdateWorkflow]: {
		readonlyTitle: readonlyTitleMessages.updateRequestTypeWorkflow,
		readonlyProduct: 'jsm',
		renderer: JsmUpdateRequestTypeWorkflowAction,
	},
	[ActionKeys.JsmSelectHardwareTypes]: {
		readonlyTitle: readonlyTitleMessages.selectHardwareTypes,
		readonlyProduct: 'jsm',
		condition: () => fg('itam_ftw'),
		renderer: (props) => {
			di(FlagsProvider, JsmSelectHardwareTypesAction);
			return <JsmSelectHardwareTypesAction {...props} />;
		},
	},
	[ActionKeys.JsmSelectHardwareTypesSkill]: {
		readonlyTitle: readonlyTitleMessages.selectHardwareTypes,
		readonlyProduct: 'jsm',
		condition: () => fg('itam_ftw'),
		renderer: (props) => {
			di(FlagsProvider, JsmSelectHardwareTypesAction);
			return <JsmSelectHardwareTypesAction {...props} />;
		},
	},
	[ActionKeys.HamOnboardingSetupComplete]: hamOnboardingSetupCompleteEntry,
	[ActionKeys.HamOnboardingSetupCompleteSkill]: hamOnboardingSetupCompleteEntry,
	// Deep Research Actions
	[ActionKeys.DeepResearchCreatePageAction]: {
		readonlyTitle: readonlyTitleMessages.deepResearchReport,
		readonlyProduct: 'confluence',
		renderer: DeepResearchActions,
	},
	// Studio Actions
	[ActionKeys.StudioAutomationTriggerRuleBuild]: {
		readonlyTitle: readonlyTitleMessages.buildSolution,
		renderer: (props) => {
			di(StudioAutomationTriggerRuleBuildAction);
			return <StudioAutomationTriggerRuleBuildAction {...props} />;
		},
	},
	[ActionKeys.StudioAutomationCreateRule]: {
		readonlyTitle: readonlyTitleMessages.createAutomationRule,
		condition: () => fg('sltns-1087-automation-create-rule-card'),
		renderer: (props) => {
			di(StudioAutomationCreateRuleAction);
			return <StudioAutomationCreateRuleAction {...props} />;
		},
	},
	[ActionKeys.StudioTriggerSolutionPlan]: {
		readonlyTitle: readonlyTitleMessages.buildSolution,
		renderer: StudioTriggerSolutionBuildAction,
	},
	[ActionKeys.RequireInformationTool]: {
		readonlyTitle: readonlyTitleMessages.respondToPrompt,
		renderer: RequireInformationUIAction,
	},
	[ActionKeys.HiringManagerTool]: {
		readonlyTitle: readonlyTitleMessages.hiringManagerAction,
		renderer: (props) => {
			di(FlagsProvider);
			return fg('hiring_manager_action_button_choices_enabled') ? (
				<HiringManagerToolUIAction {...props} />
			) : null;
		},
	},
	[ActionKeys.StudioUpdateAgentDraft]: {
		readonlyTitle: readonlyTitleMessages.updateAgentDraft,
		renderer: StudioUpdateAgentDraftAction,
	},
	[ActionKeys.UpdateAgentConfigurationTool]: {
		readonlyTitle: readonlyTitleMessages.updateAgentConfiguration,
		renderer: UpdateAgentConfigurationAction,
	},
	[ActionKeys.StudioWorkspaceUpdateAgentDraft]: {
		readonlyTitle: readonlyTitleMessages.updateAgentDraft,
		renderer: StudioWorkspaceUpdateAgentDraftAction,
	},
	[ActionKeys.StudioCreateAgent]: {
		readonlyTitle: readonlyTitleMessages.createAgent,
		renderer: (props) => <StudioCreateAgentRenderer {...props} />,
	},
	[ActionKeys.StoreRecommendationCard]: {
		readonlyTitle: readonlyTitleMessages.viewRecommendation,
		renderer: (props) => {
			di(FlagsProvider);
			return fg('rovo_chat_store_recommendation_action') ? (
				<StoreRecommendationAction {...props} />
			) : undefined;
		},
	},
	[ActionKeys.StudioLandingPageRedirect]: {
		readonlyTitle: readonlyTitleMessages.openStudio,
		renderer: (props) => {
			di(StudioLandingPageRedirect);
			return <StudioLandingPageRedirect {...props} />;
		},
	},
	[ActionKeys.AtlassianTeamUpdateArchive]: {
		readonlyTitle: readonlyTitleMessages.archiveTeam,
		renderer: (props) => {
			di(FlagsProvider);
			return fg('enable_clean_up_teams_agentic_skill') ? (
				<AtlassianTeamUpdateArchiveAction {...props} />
			) : undefined;
		},
		getAdoptedObjects: (action) => [{ objectType: 'team', objectId: action.data.teamId }],
	},
	[ActionKeys.FocusCreateAsk]: {
		readonlyTitle: readonlyTitleMessages.createAsks,
		readonlyProduct: 'focus-ask',
		renderer: (props) => {
			di(FlagsProvider, FocusAskCreateAction);
			if (!fg('display_components_for_create-focus-ask_skill') || !props.orgId) {
				return undefined;
			}
			return <FocusAskCreateAction {...props} orgId={props.orgId} />;
		},
	},
	[ActionKeys.ScheduledChat]: {
		readonlyTitle: readonlyTitleMessages.scheduleChat,
		readonlyProduct: 'schedule',
		renderer: (props) =>
			// eslint-disable-next-line @atlaskit/platform/use-recommended-utils
			FeatureGates.getExperimentValue('rovo_chat_scheduled_chat', 'isEnabled', false) ? (
				<ScheduledChatAction {...props} />
			) : undefined,
	},
	[ActionKeys.MultipleChoiceQuestionsTool]: {
		readonlyTitle: readonlyTitleMessages.reviewChoices,
		readonlyProduct: 'ask-question',
		renderer: (props) =>
			fg('jira-ai-agentic-planning-technical-changes') ? (
				<MultipleChoiceQuestionsToolInChat
					{...props}
					AskQuestionCompletedView={AskQuestionCompletedView}
				/>
			) : undefined,
	},
	[ActionKeys.AgenticPlanTool]: {
		readonlyTitle: readonlyTitleMessages.completePlan,
		readonlyProduct: 'plan',
		renderer: (props) =>
			fg('jira-ai-agentic-planning-technical-changes') ? (
				<AgenticPlanToolInChat {...props} />
			) : undefined,
	},
	// Convo AI agent tools
	[ActionKeys.ExecutePython]: {
		readonlyTitle: readonlyTitleMessages.respondToPrompt,
		renderer: ExecutePythonRendererAsync,
	},
	[ActionKeys.PlanComplete]: {
		readonlyTitle: readonlyTitleMessages.completePlan,
		readonlyProduct: 'plan',
		readonlyRenderer: PlanCompleteReadOnlyRendererAsync,
		renderer: PlanCompleteRendererAsync,
	},
	[ActionKeys.AskQuestion]: {
		readonlyTitle: readonlyTitleMessages.askQuestion,
		readonlyProduct: 'ask-question',
		readonlyRenderer: AskQuestionReadOnlyRendererAsync,
		renderer: AskQuestionRendererAsync,
	},
	[ActionKeys.ImageSearch]: {
		readonlyTitle: readonlyTitleMessages.searchImages,
		renderer: ImageSearchActionAsync,
	},
	// Bitbucket
	[ActionKeys.BitbucketPullRequestComment]: {
		readonlyTitle: readonlyTitleMessages.commentOnPullRequest,
		renderer: undefined,
		getAdoptedObjects: (action) => [
			{
				objectType: 'pull-request',
				objectId: action.data.pullRequestId,
			},
		],
	},
	[ActionKeys.CreateKBCard]: {
		readonlyTitle: readonlyTitleMessages.createKnowledgeBaseContent,
		renderer: CreateKBCard,
	},
	[ActionKeys.CreateKbArticlesCard]: {
		readonlyTitle: readonlyTitleMessages.createKnowledgeBaseContent,
		renderer: CreateKBCard,
	},
	[ActionKeys.CreateCustomSkillTool]: {
		readonlyTitle: readonlyTitleMessages.createCustomSkill,
		renderer: (props) => {
			if (!fg('studio_custom_skills_m1_frontend')) return undefined;
			return <StudioCustomSkillRenderer {...props} />;
		},
	},
	[ActionKeys.UpdateCustomSkillTool]: {
		readonlyTitle: readonlyTitleMessages.updateCustomSkill,
		renderer: (props) => {
			if (!fg('studio_custom_skills_m1_frontend')) return undefined;
			return <StudioCustomSkillRenderer {...props} />;
		},
	},
};

const jiraPlansRendererEntries = [
	{
		anthropicKey: 'execute_is.jira_atlassian_plan_update_work_sources',
		legacyKey: ActionKeys.JiraPlansUpdateWorkSource,
		registryEntry: {
			readonlyTitle: readonlyTitleMessages.updateWorkItems,
			renderer: UpdateWorkSourceAction,
		},
	},
	{
		anthropicKey: 'execute_is.jira_atlassian_plan_add_program_board',
		legacyKey: ActionKeys.JiraPlansCreateProgramBoard,
		registryEntry: {
			readonlyTitle: readonlyTitleMessages.createView,
			renderer: AddProgramBoardAction,
		},
	},
	{
		anthropicKey: 'execute_is.jira_atlassian_plan_delete_program_board',
		legacyKey: ActionKeys.JiraPlansDeleteProgramBoard,
		registryEntry: {
			readonlyTitle: readonlyTitleMessages.updateWorkItems,
			renderer: DeleteProgramBoardAction,
		},
	},
	{
		anthropicKey: 'execute_is.jira_atlassian_plan_create_work_item',
		legacyKey: ActionKeys.JiraPlansCreateWorkItem,
		registryEntry: {
			readonlyTitle: readonlyTitleMessages.createWorkItem,
			renderer: CreateWorkItemAction,
		},
	},
	{
		anthropicKey: 'execute_is.jira_atlassian_plan_create_release',
		legacyKey: ActionKeys.JiraPlansCreateRelease,
		registryEntry: {
			readonlyTitle: readonlyTitleMessages.createRelease,
			renderer: CreateReleaseAction,
		},
	},
	{
		anthropicKey: 'execute_is.jira_atlassian_plan_set_release_enabled',
		legacyKey: ActionKeys.JiraPlansManageFeatures,
		registryEntry: {
			readonlyTitle: readonlyTitleMessages.manageCapacityPlan,
			renderer: ManageFeaturesAction,
		},
	},
	{
		anthropicKey: 'execute_is.jira_atlassian_plan_add_work_item_dependencies',
		legacyKey: ActionKeys.JiraPlansAddDependency,
		registryEntry: {
			readonlyTitle: readonlyTitleMessages.updateWorkItems,
			renderer: AddDependencyAction,
		},
	},
	{
		anthropicKey: 'execute_is.jira_atlassian_plan_remove_work_item_dependencies',
		legacyKey: ActionKeys.JiraPlansRemoveDependency,
		registryEntry: {
			readonlyTitle: readonlyTitleMessages.updateWorkItems,
			renderer: RemoveDependencyAction,
		},
	},
] as const satisfies ReadonlyArray<{
	anthropicKey: string;
	legacyKey: string;
	registryEntry: Pick<ActionRendererRegistryEntry<unknown>, 'readonlyTitle'> &
		Record<string, unknown>;
}>;

const jiraPlansRegistry = Object.fromEntries(
	jiraPlansRendererEntries.flatMap(({ anthropicKey, legacyKey, registryEntry }) => [
		[
			anthropicKey,
			{
				...registryEntry,
				condition: () => fg('rovo_chat_jira_plans_anthropic_migration'),
			},
		],
		[
			anthropicKey.replace('execute_is.', 'is.'),
			{
				...registryEntry,
				condition: () => fg('rovo_chat_jira_plans_anthropic_migration'),
			},
		],
		[
			legacyKey,
			{
				...registryEntry,
				condition: () => !fg('rovo_chat_jira_plans_anthropic_migration'),
			},
		],
	]),
) as ActionRendererRegistry<ActionRendererKeyToPayload>;

const createPredicateRegistry = (
	getRovoToolsResumptionMigrationEnabled: GetRovoToolsResumptionMigrationEnabled,
): ActionRendererPredicateRegistry<ActionRendererPredicateKeyToPayload> => ({
	[ActionPredicateKeys.Resumption]: {
		readonlyTitle: readonlyTitleMessages.respondToPrompt,
		predicate: (actions: MessageAction[]) =>
			actions.every(
				(action) =>
					Boolean(action.toolSource) &&
					!(
						action.key === ActionKeys.JiraCreateSprint ||
						action.key === ActionKeys.JiraStartSprint ||
						action.key === ActionKeys.JiraMoveToSprint ||
						(action.key === ActionKeys.GoogleDocCreate && !getRovoToolsResumptionMigrationEnabled())
					) &&
					!(
						jiraPlansActionData.isLegacyActionKey(action.key) ||
						jiraPlansActionData.isCustomUiActionKey(action.key)
					) &&
					!(
						action.key === ActionKeys.ConfluenceFolderCreate &&
						fg('confluence_folder_create_action_custom_ui')
					),
			),
		renderer: ResumptionRendererAsync,
	},
});

const defaultGetActionRenderer = {
	ActionRenderer: undefined,
	ActionRendererFallback: undefined,
	readonlyTitle: undefined,
	ReadOnlyActionRenderer: undefined,
};

function logAndFallback(result: ActionValidationFailure, action: MessageAction) {
	logException({
		exception: result.error,
		name: 'conversation-assistant.useActionRenderer.getActionRenderer.validation-failed',
		tags: {
			component: 'useActionRenderer',
			actionKey: action.key,
			invocationId: action.invocationId,
			actionId: String(action.actionId),
			status: String(action.status),
		},
	});
	return defaultGetActionRenderer;
}

const liveChatRegistry: ActionRendererRegistry<ActionRendererKeyToPayload> = {
	[ActionKeys.JsmLivechat]: {
		readonlyTitle: readonlyTitleMessages.createRequest,
		readonlyProduct: 'jsm',
		renderer: JsmLivechatAction,
	},
};

const createActionRendererRegistry = (
	getRovoToolsResumptionMigrationEnabled: GetRovoToolsResumptionMigrationEnabled = defaultGetRovoToolsResumptionMigrationEnabled,
): ActionRendererRegistry<ActionRendererKeyToPayload> => ({
	...jiraGatedRegistery,
	...unifiedRegistery,
	...createRovoToolsResumptionMigrationRegistry(getRovoToolsResumptionMigrationEnabled),
	...jiraPlansRegistry,
	...liveChatRegistry,
});

let defaultActionRendererRegistry: ActionRendererRegistry<ActionRendererKeyToPayload> | undefined;

const getDefaultActionRendererRegistry = (): ActionRendererRegistry<ActionRendererKeyToPayload> => {
	defaultActionRendererRegistry ??= createActionRendererRegistry();
	return defaultActionRendererRegistry;
};

const streamActionReadonlyTitleByKey: Record<string, MessageDescriptor> = {
	[CONFLUENCE_AIFC_PAGE_CREATE_KEY]: readonlyTitleMessages.createPage,
	[CONFLUENCE_AIFC_DEEP_RESEARCH_PAGE_CREATE_KEY]: readonlyTitleMessages.deepResearchReport,
	[CONFLUENCE_AIFC_PAGE_UPDATE_KEY]: readonlyTitleMessages.editContent,
	[CONFLUENCE_AIFC_WHITEBOARD_CREATE_KEY]: readonlyTitleMessages.createWhiteboard,
	[CONFLUENCE_AIFC_WHITEBOARD_UPDATE_KEY]: readonlyTitleMessages.updateWhiteboard,
	[CONFLUENCE_AIFC_WHITEBOARD_TO_PAGE_KEY]: readonlyTitleMessages.createPage,
	[CONFLUENCE_WHITEBOARD_BRAINSTORMING_KEY]: readonlyTitleMessages.updateWhiteboard,
	[LEGACY_CONFLUENCE_WHITEBOARD_DIAGRAMMING_KEY]: readonlyTitleMessages.updateWhiteboard,
	[AIFC_CONFLUENCE_WHITEBOARD_DIAGRAMMING_KEY]: readonlyTitleMessages.updateWhiteboard,
	[CONFLUENCE_AIFC_SLIDES_CREATE_KEY]: readonlyTitleMessages.createSlides,
	[CONFLUENCE_AIFC_SLIDES_UPDATE_KEY]: readonlyTitleMessages.updateSlides,
	[CONFLUENCE_AIFC_DATABASE_CREATE_KEY]: readonlyTitleMessages.createDatabase,
	[CONFLUENCE_AIFC_DATABASE_UPDATE_KEY]: readonlyTitleMessages.updateDatabase,
	[CONFLUENCE_AIFC_SPACE_CREATE_KEY]: readonlyTitleMessages.createSpace,
	[CONFLUENCE_AIFC_SPACE_UPDATE_KEY]: readonlyTitleMessages.updateSpace,
	[DYNAMIC_UI_CREATE_KEY]: readonlyTitleMessages.createDynamicUi,
	[IMAGE_CREATE_KEY]: readonlyTitleMessages.createImage,
	[JSM_CREATE_SURVEY_KEY]: readonlyTitleMessages.createSurvey,
};

const streamActionReadonlyProductByKey: Record<string, ActionRendererProduct> = {
	[CONFLUENCE_AIFC_PAGE_CREATE_KEY]: 'confluence',
	[CONFLUENCE_AIFC_DEEP_RESEARCH_PAGE_CREATE_KEY]: 'confluence',
	[CONFLUENCE_AIFC_PAGE_UPDATE_KEY]: 'confluence',
	[CONFLUENCE_AIFC_WHITEBOARD_CREATE_KEY]: 'confluence',
	[CONFLUENCE_AIFC_WHITEBOARD_UPDATE_KEY]: 'confluence',
	[CONFLUENCE_AIFC_WHITEBOARD_TO_PAGE_KEY]: 'confluence',
	[CONFLUENCE_WHITEBOARD_BRAINSTORMING_KEY]: 'confluence',
	[LEGACY_CONFLUENCE_WHITEBOARD_DIAGRAMMING_KEY]: 'confluence',
	[AIFC_CONFLUENCE_WHITEBOARD_DIAGRAMMING_KEY]: 'confluence',
	[CONFLUENCE_AIFC_SLIDES_CREATE_KEY]: 'confluence',
	[CONFLUENCE_AIFC_SLIDES_UPDATE_KEY]: 'confluence',
	[CONFLUENCE_AIFC_DATABASE_CREATE_KEY]: 'confluence',
	[CONFLUENCE_AIFC_DATABASE_UPDATE_KEY]: 'confluence',
	[CONFLUENCE_AIFC_SPACE_CREATE_KEY]: 'confluence',
	[CONFLUENCE_AIFC_SPACE_UPDATE_KEY]: 'confluence',
	[DYNAMIC_UI_CREATE_KEY]: 'confluence',
	[IMAGE_CREATE_KEY]: 'confluence',
	[JSM_CREATE_SURVEY_KEY]: 'jsm',
};

const inferReadonlyActionProduct = (actionKey: string): ActionRendererProduct | undefined => {
	const normalizedActionKey = actionKey.toLowerCase();
	if (
		normalizedActionKey.includes('jira') ||
		normalizedActionKey.includes('work-item') ||
		normalizedActionKey.includes('issue')
	) {
		return 'jira';
	}
	if (normalizedActionKey.includes('confluence')) {
		return 'confluence';
	}
	if (normalizedActionKey.includes('jsm') || normalizedActionKey.includes('request-type')) {
		return 'jsm';
	}
	return undefined;
};

export const getReadonlyActionTitle = (actionKey: string): MessageDescriptor | undefined => {
	const registeredTitle =
		getDefaultActionRendererRegistry()[actionKey as keyof ActionRendererKeyToPayload]
			?.readonlyTitle;

	return registeredTitle ?? streamActionReadonlyTitleByKey[actionKey];
};

export const getReadonlyActionProduct = (actionKey: string): ActionRendererProduct | undefined => {
	const registeredProduct =
		getDefaultActionRendererRegistry()[actionKey as keyof ActionRendererKeyToPayload]
			?.readonlyProduct;

	return (
		registeredProduct ??
		streamActionReadonlyProductByKey[actionKey] ??
		inferReadonlyActionProduct(actionKey)
	);
};

const isObject = (value: unknown): value is Record<string, unknown> =>
	typeof value === 'object' && value !== null;

const hasReadonlyPlanCompleteData = (action: MessageAction): boolean =>
	(isObject(action.data) && typeof action.data.plan === 'string') ||
	(typeof action.confirmationMessage === 'string' && action.confirmationMessage.trim().length > 0);

const canRenderReadonlyAction = (action: MessageAction): boolean => {
	if (action.key === ActionKeys.PlanComplete) {
		return hasReadonlyPlanCompleteData(action);
	}

	return true;
};

export const getReadonlyActionRenderer = (
	action: MessageAction,
): OpaqueReadOnlyActionRenderer | undefined => {
	const registryEntry =
		getDefaultActionRendererRegistry()[action.key as keyof ActionRendererKeyToPayload];

	if (!canRenderReadonlyAction(action)) {
		return undefined;
	}

	if (registryEntry?.condition?.()) {
		return registryEntry.readonlyRenderer;
	}

	if (registryEntry?.condition) {
		return undefined;
	}

	return registryEntry?.readonlyRenderer;
};

export const useActionRenderer = (
	getRovoToolsResumptionMigrationEnabled: GetRovoToolsResumptionMigrationEnabled = defaultGetRovoToolsResumptionMigrationEnabled,
): {
	getActionRenderer: GetOpaqueActionRenderer;
} => {
	const registry = useMemo<ActionRendererRegistry<ActionRendererKeyToPayload>>(
		() => createActionRendererRegistry(getRovoToolsResumptionMigrationEnabled),
		[getRovoToolsResumptionMigrationEnabled],
	);

	const predicateRegistry = useMemo(
		() => createPredicateRegistry(getRovoToolsResumptionMigrationEnabled),
		[getRovoToolsResumptionMigrationEnabled],
	);

	const { getActionRenderer: getActionRendererWithRegistry } = useActionRendererWithRegistry(
		registry,
		predicateRegistry,
	);

	const getActionRenderer: GetOpaqueActionRenderer = useCallback(
		(actionKey: string, actions: MessageAction[]) => {
			try {
				return getActionRendererWithRegistry(actionKey, actions);
			} catch (error) {
				if (isActionValidationFailureError(error)) {
					return logAndFallback(error.validationFailure, error.action);
				}
				return defaultGetActionRenderer;
			}
		},
		[getActionRendererWithRegistry],
	);

	return { getActionRenderer };
};
